import { IconType } from 'react-icons';
import { AiOutlineDashboard, AiOutlineUser } from 'react-icons/ai';
import { BsBuildings, BsGear } from 'react-icons/bs';
import { FiFileText, FiHeadphones, FiHome, FiUsers } from 'react-icons/fi';
import { IoWalletOutline } from 'react-icons/io5';
import { PiPlugCharging } from 'react-icons/pi';
import { RiBillLine, RiShieldUserLine } from 'react-icons/ri';
import { AppPermissions } from './hooks/use-permission-hook';

export type PermissionKey = keyof AppPermissions;
export type CommandRoleLevel = 'super' | 'company' | 'estate';

export type Command = {
  id: string;
  title: string;
  description: string;
  category: string;
  route: string;
  keywords: string[];
  icon?: IconType;
  permissions?: PermissionKey[];
  permissionMode?: 'any' | 'all';
  roleLevels?: CommandRoleLevel[];
};

export const normalizeCommandQuery = (value: string) =>
  value
    .toLowerCase()
    .replace(/[_/-]+/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const page = (command: Command) => command;

export const commands: Command[] = [
  page({
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Home analytics and operational summary',
    category: 'Navigation',
    route: '/',
    keywords: ['home', 'overview', 'analytics', 'summary'],
    permissions: ['canViewHomeAnalytics'],
    icon: AiOutlineDashboard,
  }),
  page({
    id: 'companies',
    title: 'Companies',
    description: 'Manage estate companies',
    category: 'Estate Management',
    route: '/companies',
    keywords: ['company', 'companies', 'organisation', 'organization'],
    permissions: ['canListCompanies', 'canViewCompany'],
    roleLevels: ['super'],
    icon: BsBuildings,
  }),
  page({
    id: 'create-company',
    title: 'Create Company',
    description: 'Open companies to add a new company',
    category: 'Estate Actions',
    route: '/companies',
    keywords: ['create company', 'new company', 'add company', 'register company'],
    permissions: ['canCreateCompany'],
    roleLevels: ['super'],
    icon: BsBuildings,
  }),
  page({
    id: 'estates',
    title: 'Estates',
    description: 'View and manage estates',
    category: 'Estate Management',
    route: '/estates',
    keywords: ['estate', 'estates', 'communities'],
    permissions: ['canListEstates', 'canViewEstate'],
    roleLevels: ['super', 'company'],
    icon: FiHome,
  }),
  page({
    id: 'create-estate',
    title: 'Create Estate',
    description: 'Open estates to add a new estate',
    category: 'Estate Actions',
    route: '/estates',
    keywords: ['create estate', 'new estate', 'add estate', 'register estate'],
    permissions: ['canCreateEstate'],
    roleLevels: ['super', 'company'],
    icon: FiHome,
  }),
  page({
    id: 'property-units',
    title: 'Property Units',
    description: 'Apartments, homes, and estate units',
    category: 'Estate Management',
    route: '/properties',
    keywords: ['property', 'properties', 'unit', 'units', 'apartment', 'house', 'resident unit'],
    permissions: ['canListUnits', 'canViewUnit'],
    icon: FiUsers,
  }),
  page({
    id: 'create-property-unit',
    title: 'Create Property Unit',
    description: 'Open property units to add a unit',
    category: 'Estate Actions',
    route: '/properties',
    keywords: ['create property', 'create unit', 'new unit', 'add unit', 'add property unit'],
    permissions: ['canCreateUnit'],
    icon: FiUsers,
  }),
  page({
    id: 'wallet-funding',
    title: 'Wallet Funding',
    description: 'Fund resident wallets and review wallet activity',
    category: 'Estate Management',
    route: '/wallet-fund',
    keywords: ['wallet', 'fund wallet', 'wallet funding', 'funding', 'top up', 'balance'],
    permissions: ['canListWallets', 'canViewWallet', 'canFundWallet'],
    icon: IoWalletOutline,
  }),
  page({
    id: 'fund-wallet',
    title: 'Fund Wallet',
    description: 'Open wallet funding to fund a resident wallet',
    category: 'Estate Actions',
    route: '/wallet-fund',
    keywords: ['fund wallet', 'wallet top up', 'add wallet balance', 'credit wallet'],
    permissions: ['canFundWallet'],
    icon: IoWalletOutline,
  }),
  page({
    id: 'bill-items',
    title: 'Billing Items',
    description: 'Billable items used when creating bills',
    category: 'Billing Management',
    route: '/bill-items',
    keywords: ['billing item', 'billing items', 'bill item', 'billable item', 'billable items', 'charges', 'fees', 'levy', 'service charge'],
    permissions: ['canListBillingItems', 'canViewBillingItem'],
    icon: RiBillLine,
  }),
  page({
    id: 'create-billing-item',
    title: 'Create Billing Item',
    description: 'Open billing items to add a new billable item',
    category: 'Billing Actions',
    route: '/bill-items',
    keywords: ['create billing item', 'create bill item', 'new billing item', 'new bill item', 'add billable item', 'create charge'],
    permissions: ['canCreateBillingItem'],
    icon: RiBillLine,
  }),
  page({
    id: 'bill-templates',
    title: 'Billing Templates',
    description: 'Reusable bill templates for estate invoices',
    category: 'Billing Management',
    route: '/bill-templates',
    keywords: ['bill templates', 'billing templates', 'invoice templates', 'draft bill'],
    permissions: ['canListBillingTemplates', 'canViewBillingTemplate'],
    icon: RiBillLine,
  }),
  page({
    id: 'create-billing-template',
    title: 'Create Billing Template',
    description: 'Create a reusable billing template',
    category: 'Billing Actions',
    route: '/bill-templates?section=create',
    keywords: ['create template', 'new template', 'create bill template', 'create billing template'],
    permissions: ['canCreateBillingTemplate'],
    icon: RiBillLine,
  }),
  page({
    id: 'invoices',
    title: 'Issued Invoices',
    description: 'View issued invoices',
    category: 'Billing Management',
    route: '/bill-invoices?tab=issued',
    keywords: ['invoice', 'invoices', 'issued invoice', 'invoice list', 'bills'],
    permissions: ['canListInvoices', 'canViewInvoice'],
    icon: FiFileText,
  }),
  page({
    id: 'draft-invoices',
    title: 'Draft Invoices',
    description: 'View saved invoice drafts',
    category: 'Billing Management',
    route: '/bill-invoices?tab=draft',
    keywords: ['draft invoice', 'invoice draft', 'saved invoice', 'draft bills'],
    permissions: ['canListInvoiceTemplates', 'canViewInvoiceTemplate'],
    icon: FiFileText,
  }),
  page({
    id: 'create-invoice',
    title: 'Create Invoice',
    description: 'Open the invoice creation flow',
    category: 'Billing Actions',
    route: '/bill-invoices?section=issue',
    keywords: ['create invoice', 'issue invoice', 'generate invoice', 'new invoice', 'send invoice', 'create bill', 'issue bill'],
    permissions: ['canGenerateInvoice'],
    icon: RiBillLine,
  }),
  page({
    id: 'manual-invoice',
    title: 'Manual Invoice',
    description: 'Open invoices to issue a manual invoice',
    category: 'Billing Actions',
    route: '/bill-invoices?tab=issued',
    keywords: ['manual invoice', 'issue manual invoice', 'generate manual invoice'],
    permissions: ['canGenerateManualInvoice'],
    icon: RiBillLine,
  }),
  page({
    id: 'vend-property-unit',
    title: 'Vending Property Unit',
    description: 'Vend electricity tokens for property units',
    category: 'Vending Management',
    route: '/vend-unit',
    keywords: ['vend unit', 'vending unit', 'property unit token', 'generate unit token', 'meter token'],
    permissions: ['canListPropertyUnitTokens', 'canViewPropertyUnitToken'],
    icon: PiPlugCharging,
  }),
  page({
    id: 'generate-property-unit-token',
    title: 'Generate Property Unit Token',
    description: 'Open property unit vending to generate a token',
    category: 'Vending Actions',
    route: '/vend-unit',
    keywords: ['generate token', 'generate unit token', 'vend property', 'vend a unit', 'property token'],
    permissions: ['canGeneratePropertyUnitToken'],
    icon: PiPlugCharging,
  }),
  page({
    id: 'vend-common-area',
    title: 'Vending Common Area',
    description: 'Vend electricity tokens for common areas',
    category: 'Vending Management',
    route: '/vend-area',
    keywords: ['vend area', 'common area', 'common area token', 'vending common area'],
    permissions: ['canListCommonAreaTokens', 'canViewCommonAreaToken'],
    icon: PiPlugCharging,
  }),
  page({
    id: 'generate-common-area-token',
    title: 'Generate Common Area Token',
    description: 'Open common area vending to generate a token',
    category: 'Vending Actions',
    route: '/vend-area',
    keywords: ['generate common area token', 'vend common area', 'common token', 'area token'],
    permissions: ['canGenerateCommonAreaToken'],
    icon: PiPlugCharging,
  }),
  page({
    id: 'emergency-token',
    title: 'Emergency Token',
    description: 'Issue and view emergency tokens',
    category: 'Vending Management',
    route: '/emergency-token',
    keywords: ['emergency token', 'issue emergency token', 'generate emergency token'],
    permissions: ['canListEmergencyTokens', 'canViewEmergencyToken'],
    icon: RiShieldUserLine,
  }),
  page({
    id: 'issue-emergency-token',
    title: 'Issue Emergency Token',
    description: 'Open emergency tokens to issue a new token',
    category: 'Vending Actions',
    route: '/emergency-token',
    keywords: ['issue emergency token', 'generate emergency token', 'new emergency token'],
    permissions: ['canGenerateEmergencyToken'],
    icon: RiShieldUserLine,
  }),
  page({
    id: 'tamper-code',
    title: 'Tamper Code',
    description: 'Generate and manage tamper codes',
    category: 'Vending Management',
    route: '/tamper-code',
    keywords: ['tamper', 'tamper code', 'generate tamper code'],
    permissions: ['canListTamperCodes', 'canViewTamperCode'],
    icon: RiShieldUserLine,
  }),
  page({
    id: 'generate-tamper-code',
    title: 'Generate Tamper Code',
    description: 'Open tamper codes to generate a code',
    category: 'Vending Actions',
    route: '/tamper-code',
    keywords: ['generate tamper code', 'new tamper code', 'create tamper code'],
    permissions: ['canGenerateTamperCode'],
    icon: RiShieldUserLine,
  }),
  page({
    id: 'visitor-logs',
    title: 'Visitor Logs',
    description: 'View visitor activity and visitations',
    category: 'Visitor Management',
    route: '/visitor-logs',
    keywords: ['visitor', 'visitors', 'visitor logs', 'visitations', 'guest logs'],
    permissions: ['canListVisitations', 'canViewVisitation'],
    icon: FiUsers,
  }),
  page({
    id: 'security-staffs',
    title: 'Security Staffs',
    description: 'Manage estate security personnel',
    category: 'Visitor Management',
    route: '/security-staffs',
    keywords: ['security', 'security staff', 'guards', 'security personnel'],
    permissions: ['canListSecurities', 'canViewSecurity'],
    icon: RiShieldUserLine,
  }),
  page({
    id: 'add-security-staff',
    title: 'Add Security Staff',
    description: 'Open security staffs to add a security user',
    category: 'Visitor Actions',
    route: '/security-staffs',
    keywords: ['add security', 'create security', 'new security staff', 'add guard'],
    permissions: ['canCreateSecurity'],
    icon: RiShieldUserLine,
  }),
  page({
    id: 'reports',
    title: 'All Transactions Report',
    description: 'Open the all transactions report tab',
    category: 'Reports',
    route: '/reports?section=transaction',
    keywords: ['reports', 'transaction report', 'all transaction report', 'transactions report'],
    permissions: ['canListAllTransactionsReport'],
    icon: FiFileText,
  }),
  page({
    id: 'vending-reports',
    title: 'Vending Reports',
    description: 'Open the vending reports tab',
    category: 'Reports',
    route: '/reports?section=vending',
    keywords: ['vending report', 'vending reports', 'vend report', 'meter report'],
    permissions: ['canListVendingTransactionsReport'],
    icon: FiFileText,
  }),
  page({
    id: 'invoice-reports',
    title: 'Invoice Reports',
    description: 'Open the invoice reports tab',
    category: 'Reports',
    route: '/reports?section=invoice_auto',
    keywords: ['invoice report', 'invoice reports', 'automatic invoice report', 'bill report'],
    permissions: ['canListInvoiceReport', 'canListInvoiceTransactionsReport'],
    icon: FiFileText,
  }),
  page({
    id: 'manual-invoice-reports',
    title: 'Invoice Manual Posting Reports',
    description: 'Open the manual invoice posting reports tab',
    category: 'Reports',
    route: '/reports?section=invoice_manual',
    keywords: ['manual invoice report', 'invoice manual posting', 'manual posting report'],
    permissions: ['canListInvoiceReport', 'canListInvoiceTransactionsReport'],
    icon: FiFileText,
  }),
  page({
    id: 'wallet-reports',
    title: 'Wallet Reports',
    description: 'Open the wallet reports tab',
    category: 'Reports',
    route: '/reports?section=wallet',
    keywords: ['wallet report', 'wallet reports', 'wallet balance report', 'funding report'],
    permissions: ['canListWalletBalanceSummariesReport'],
    icon: IoWalletOutline,
  }),
  page({
    id: 'invoice-notifications',
    title: 'Invoice Notifications',
    description: 'Open the invoice notifications report tab',
    category: 'Reports',
    route: '/reports?section=invoice',
    keywords: ['invoice notification', 'invoice notifications', 'notification report'],
    permissions: ['canNotifyUnits', 'canListInvoiceReport'],
    icon: FiFileText,
  }),
  page({
    id: 'users',
    title: 'All Users',
    description: 'View residents, occupants, and users',
    category: 'Users Management',
    route: '/users-mgt?section=all_users',
    keywords: ['users', 'all users', 'occupants', 'residents', 'members'],
    permissions: ['canListUsers', 'canViewUser'],
    icon: AiOutlineUser,
  }),
  page({
    id: 'create-user',
    title: 'Create User',
    description: 'Open users to create a new occupant',
    category: 'Users Actions',
    route: '/users-mgt?section=all_users',
    keywords: ['create user', 'new user', 'create occupant', 'add occupant', 'add resident'],
    permissions: ['canCreateUser'],
    icon: AiOutlineUser,
  }),
  page({
    id: 'pending-users',
    title: 'Pending Users',
    description: 'Review users awaiting approval',
    category: 'Users Management',
    route: '/users-mgt?section=pending',
    keywords: ['pending users', 'pending approval', 'approve user', 'reservations'],
    permissions: ['canApproveRejectUserReservation', 'canListUsers'],
    icon: AiOutlineUser,
  }),
  page({
    id: 'settings-profile',
    title: 'General Profile',
    description: 'Open profile settings',
    category: 'Settings',
    route: '/settings?section=profile',
    keywords: ['settings', 'profile', 'general profile', 'my profile'],
    permissions: ['canViewProfile'],
    icon: BsGear,
  }),
  page({
    id: 'settings-vending',
    title: 'Vending Settings',
    description: 'Configure vending prices',
    category: 'Settings',
    route: '/settings?section=vending',
    keywords: ['vending settings', 'price settings', 'vending price', 'tariff'],
    permissions: ['canListPriceSettings', 'canViewPriceSettings'],
    roleLevels: ['super'],
    icon: BsGear,
  }),
  page({
    id: 'settings-admin-management',
    title: 'Admin Management',
    description: 'Manage admins, roles, and permissions',
    category: 'Settings',
    route: '/settings?section=admin_management',
    keywords: ['admin', 'admins', 'roles', 'permissions', 'admin management', 'create admin'],
    permissions: ['canListAdmins', 'canViewAdmin', 'canListRoles', 'canListPermissions'],
    roleLevels: ['super', 'company'],
    icon: AiOutlineUser,
  }),
  page({
    id: 'create-admin',
    title: 'Create Admin',
    description: 'Open admin management to create an admin',
    category: 'Settings Actions',
    route: '/settings?section=admin_management',
    keywords: ['create admin', 'new admin', 'invite admin', 'add admin'],
    permissions: ['canCreateAdmin'],
    roleLevels: ['super', 'company'],
    icon: AiOutlineUser,
  }),
  page({
    id: 'settings-meter-management',
    title: 'Meter Management',
    description: 'Manage meter configuration',
    category: 'Settings',
    route: '/settings?section=meter_management',
    keywords: ['meter', 'meters', 'meter management', 'configure meter', 'meter settings'],
    permissions: ['canListMeters', 'canViewMeter'],
    icon: BsGear,
  }),
  page({
    id: 'configure-meter',
    title: 'Configure Meter',
    description: 'Open meter management to configure meters',
    category: 'Settings Actions',
    route: '/settings?section=meter_management',
    keywords: ['configure meter', 'setup meter', 'meter configuration', 'update meter'],
    permissions: ['canUpdateMeter'],
    icon: BsGear,
  }),
  page({
    id: 'settings-payment-gateways',
    title: 'Payment Gateways',
    description: 'Configure payment gateway options',
    category: 'Settings',
    route: '/settings?section=payments',
    keywords: ['payment', 'payments', 'gateway', 'payment gateways', 'set payment options'],
    permissions: ['canListPaymentGateways', 'canViewPaymentGateway'],
    roleLevels: ['super'],
    icon: BsGear,
  }),
  page({
    id: 'set-payment-options',
    title: 'Set Payment Options',
    description: 'Open payment gateways to configure payment options',
    category: 'Settings Actions',
    route: '/settings?section=payments',
    keywords: ['set payment options', 'create payment gateway', 'add payment gateway', 'configure payment'],
    permissions: ['canCreatePaymentGateway', 'canUpdatePaymentGateway'],
    roleLevels: ['super'],
    icon: BsGear,
  }),
  page({
    id: 'settings-banks',
    title: 'Banks',
    description: 'Manage bank setup',
    category: 'Settings',
    route: '/settings?section=banks',
    keywords: ['bank', 'banks', 'bank account', 'settlement bank'],
    permissions: ['canListAccountSetups', 'canViewAccountSetup'],
    roleLevels: ['super'],
    icon: BsBuildings,
  }),
  page({
    id: 'settings-account-setup',
    title: 'Account Setup',
    description: 'Manage account setup records',
    category: 'Settings',
    route: '/settings?section=acc_setup',
    keywords: ['account setup', 'account settings', 'setup account'],
    permissions: ['canListAccountSetups', 'canViewAccountSetup'],
    roleLevels: ['super'],
    icon: BsGear,
  }),
  page({
    id: 'settings-chart-of-account',
    title: 'Chart Of Account',
    description: 'Manage chart of account settings',
    category: 'Settings',
    route: '/settings?section=coa',
    keywords: ['chart of account', 'coa', 'ledger', 'accounts chart'],
    permissions: ['canListChartOfAccounts', 'canViewChartOfAccounts'],
    roleLevels: ['super'],
    icon: BsGear,
  }),
  page({
    id: 'set-chart-of-account',
    title: 'Set Chart Of Account',
    description: 'Open chart of account settings to create or update accounts',
    category: 'Settings Actions',
    route: '/settings?section=coa',
    keywords: ['set chart of account', 'create chart of account', 'create coa', 'set coa'],
    permissions: ['canCreateChartOfAccount', 'canUpdateChartOfAccount'],
    roleLevels: ['super'],
    icon: BsGear,
  }),
  page({
    id: 'support',
    title: 'Support',
    description: 'Complaints and support tickets',
    category: 'Support',
    route: '/support',
    keywords: ['support', 'complaints', 'tickets', 'help desk', 'issue'],
    permissions: ['canListComplaints', 'canViewComplaint'],
    icon: FiHeadphones,
  }),
];

export default commands;
